// app/features/auth/AuthProvider.tsx
//
// Wraps <Outlet/> in root.tsx. On first render, initializes MSAL, processes
// the redirect callback (if any), and bootstraps the auth store from
// /api/v1/auth/me.

import { useEffect, type FC, type ReactNode } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { toast } from "sonner";

import {
  identifyUser,
  trackAuthSignedIn,
  trackAuthSignInFailed,
} from "~/lib/analytics";
import { fetchWithAuth } from "~/lib/api";
import type { ApiUserContext } from "~/lib/types";
import type { AuthUser } from "./authStore";
import { useAuthStore } from "./authStore";
import { msalInstance } from "./msalConfig";

interface Props {
  children: ReactNode;
}

export const AuthProvider: FC<Props> = ({ children }) => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await msalInstance.initialize();
        const redirectResult = await msalInstance.handleRedirectPromise();
        const accounts = msalInstance.getAllAccounts();
        if (cancelled) return;
        if (accounts.length === 0) {
          // Diagnostic: if the URL still carries an auth hash and we got
          // zero accounts, MSAL silently failed to consume the response
          // (typically: sessionStorage state mismatch on return). Surface
          // it so it's visible in the console next time it happens.
          if (
            typeof window !== "undefined" &&
            window.location.hash.includes("code=") &&
            !redirectResult
          ) {
            // eslint-disable-next-line no-console
            console.error(
              "AuthProvider: auth hash present but MSAL produced no accounts — likely lost redirect state.",
            );
          }
          useAuthStore.getState().clear();
          return;
        }
        msalInstance.setActiveAccount(accounts[0]);
        const resp = await fetchWithAuth("/auth/me");
        if (cancelled) return;
        if (resp.ok) {
          const baseProfile = (await resp.json()) as Pick<AuthUser, "email" | "isAdmin" | "isUploader">;

          // Fetch visibility-tier context from the new /api/v1/me endpoint.
          let visibilityCtx: Pick<ApiUserContext, "isWcs" | "customerId" | "uploaderCustomerId"> = {
            isWcs: false,
            customerId: null,
            uploaderCustomerId: null,
          };
          try {
            const meResp = await fetchWithAuth("/me");
            if (meResp.ok) {
              const meJson = (await meResp.json()) as ApiUserContext;
              visibilityCtx = {
                isWcs: meJson.isWcs,
                customerId: meJson.customerId,
                uploaderCustomerId: meJson.uploaderCustomerId,
              };
            }
          } catch {
            // Non-fatal: visibility fields default to safe values above.
          }

          const profile: AuthUser = { ...baseProfile, ...visibilityCtx };
          useAuthStore.getState().setUser(profile);
          identifyUser(profile.email, {
            isAdmin: profile.isAdmin,
            isUploader: profile.isUploader,
          });
          trackAuthSignedIn({ method: "redirect" });
        } else {
          // 401/403 — token valid at Entra but our backend rejected it.
          let body: { error?: string } = {};
          try {
            body = await resp.json();
          } catch {
            /* ignore */
          }
          const reason =
            body.error === "domain_not_allowed"
              ? "domain_not_allowed"
              : resp.status === 401
                ? "token_invalid"
                : "domain_not_allowed";
          trackAuthSignInFailed({ reason });
          // We don't redirect to /login (would loop). Show no-access page by
          // setting user to null; routes.tsx renders /no-access for that state.
          useAuthStore.getState().setUser(null);
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof InteractionRequiredAuthError) {
          trackAuthSignInFailed({ reason: "token_invalid" });
        } else {
          // eslint-disable-next-line no-console
          console.error("AuthProvider bootstrap failed:", e);
          toast.error("Sign-in failed. Please try again.");
          trackAuthSignInFailed({ reason: "network_error" });
        }
        useAuthStore.getState().setUser(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <>{children}</>;
};
